/**
 * What every menu screen has in common: its clock, the header with BACK, the opening transition,
 * music, screen-reader announcements and the back action. Screens extend it and draw the rest.
 */

import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput, Scene, SceneHost, Services } from '../scene';
import { backHit, drawFooter, drawHeader, drawReveal, keyWords } from './chrome';
import { BG, setCalm } from './theme';

export type UiSound = 'move' | 'select' | 'back' | 'error' | 'type';

export abstract class UiScene implements Scene {
  /** Milliseconds since the screen was (re)entered. */
  protected ms = 0;
  /** The screen's name in the header; '' for none. */
  protected abstract readonly heading: string;
  /** The tune to play while this screen is up; null leaves the music alone. */
  protected readonly track: 'title' | 'hiscore' | null = 'title';
  /** The header's BACK button (screens that must be finished, such as Initials, hide it). */
  protected readonly showBack: boolean = true;
  private backHot = false;

  constructor(protected readonly host: SceneHost) {}

  protected get services(): Services {
    return this.host.services;
  }

  enter(): void {
    this.ms = 0;
    if (this.track) this.services.audio.music(this.track);
    this.say(this.describe());
  }

  update(dtMs: number): void {
    this.ms += dtMs;
    this.tick(dtMs);
  }

  draw(screen: Screen): void {
    setCalm(this.services.options.get().reduceFlashing);
    screen.clear(BG);
    this.paint(screen);
    if (this.heading) drawHeader(screen, this.heading, this.ms, this.showBack, this.backHot);
    const hint = this.hint();
    if (hint) drawFooter(screen, hint);
    drawReveal(screen, this.ms);
  }

  action(a: MenuAction): void {
    if (a === 'back') this.back();
    else this.onAction(a);
  }

  pointer(p: PointerInput): void {
    if (this.heading && this.showBack) {
      const hot = backHit(p.x, p.y);
      if (hot && p.kind === 'down' && p.button === 0) return this.back();
      if (p.kind === 'move') this.backHot = hot;
    }
    if (p.kind === 'down' && p.button === 2) return this.back();
    this.onPointer(p);
  }

  /** What to tell a screen reader on entering: the screen's name and the selected item. */
  protected describe(): string {
    return this.heading;
  }

  /** The footer hint, from the words for the input in use. */
  protected hint(): string {
    return '';
  }

  protected words(): { ok: string; back: string } {
    return keyWords(this.services.input.gamepad());
  }

  protected tick(_dtMs: number): void {}

  protected abstract paint(screen: Screen): void;

  protected abstract onAction(a: MenuAction): void;

  protected onPointer(_p: PointerInput): void {}

  protected back(): void {
    this.sound('back');
    this.host.back();
  }

  protected sound(s: UiSound): void {
    this.services.audio.ui(s);
  }

  protected say(text: string): void {
    if (text) this.services.announce(text);
  }
}
